import { Component, computed, Input, input, InputSignal } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { CommonModule } from '@angular/common';
import { BarChartConfig } from '@services/analytics-services/hight-chart-configurations/bar-high-chart-config';
import { PieChartConfig } from '@services/analytics-services/hight-chart-configurations/pie-high-chart-config';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { ErrorStateComponent } from '@enginuity/core/organisms/error-state/error-state.component';

// Define possible display statuses
type DisplayStatus = 'error' | 'empty' | 'data';

/**
 * Chart type enum
 */
export type ChartType = 'bar' | 'pie';

@Component({
  selector: 'app-high-chart-container',
  standalone: true,
  imports: [CommonModule, HighchartsChartModule, ErrorStateComponent],
  templateUrl: './high-chart-container.component.html',
  styleUrl: './high-chart-container.component.scss',
})
export class HighChartContainerComponent {
  @Input() headingTitle: string = '';
  @Input() subtitle: string = '';
  // Inputs converted to signals
  chartOptions: InputSignal<ChartOptions> = input<ChartOptions>({});
  chartType: InputSignal<ChartType> = input<ChartType>('bar');
  title: InputSignal<string> = input('');
  emptyStateMessage: InputSignal<string> = input('No data available');
  errorState: InputSignal<string | false> = input<string | false>(false);

  Highcharts: typeof Highcharts = Highcharts;

  // Computed signal for hasData status
  readonly hasData = computed(() => {
    const opts = this.chartOptions();
    return !this.errorState() && !(!opts?.data || opts.data.length === 0);
  });

  readonly displayStatus = computed<DisplayStatus>(() => {
    if (this.errorState()) {
      return 'error';
    } else if (!this.hasData()) {
      return 'empty';
    } else {
      return 'data';
    }
  });

  // Computed signal for final Highcharts options
  readonly finalOptions = computed(() => {
    const baseOptions = this.chartOptions();
    const type = this.chartType();
    const emptyMsg = this.emptyStateMessage();
    const dataExists = this.hasData();
    const currentErrorState = this.errorState();

    if (typeof currentErrorState === 'string') {
      return { lang: { noData: '' } } as Highcharts.Options;
    }

    let calculatedOptions: Highcharts.Options;
    switch (type) {
      case 'pie':
        calculatedOptions = PieChartConfig.getPieChartOptions(baseOptions);
        break;
      default:
        calculatedOptions = BarChartConfig.getBarChartOptions(baseOptions);
        break;
    }

    if (!dataExists) {
      calculatedOptions.lang = {
        ...calculatedOptions.lang,
        noData: emptyMsg,
      };
      calculatedOptions.noData = {
        ...calculatedOptions.noData,
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: '#303030',
        },
      };
    }

    return calculatedOptions;
  });
}
